import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { formatDate_12h } from "../../services/Utils";

const STAGE_SIGNAL_MAP = {
  version: [
    "Version matched",
    "Version check disabled",
    "Version upgradable",
    "Version compatible",
    "Version not compatible",
    "Version not upgradable",
    "Device version is higher than campaign version",
  ],
  download: [
    "Download consent popup shown",
    "User accepts download",
    "User rejects download",
    "Resume downloading",
    "All files downloaded",
    "Download failed",
    "Cancel downloading",
    "User deferred download",
    "Download authentication failed",
    "Data setting disabled on Wi-Fi",
    "Data setting enabled on Wi-Fi",
    "Storage space insufficient",
    "Ota campaign paused",
  ],
  verification: [
    "Verification started",
    "Verification success",
    "Verification failed",
    "User Accepts Verification",
    "User Rejects Verification",
  ],
  consent: [
    "Install consent popup shown",
    "Installation precondition met",
    "Installation precondition not met",
    "User accepts install",
    "User rejects install",
    "User deferred install",
  ],
  installation: [
    "Installation success",
    "Installation failed",
    "In progress",
    "Complete",
    "Ota campaign paused",
    "Version matched",
  ],
};

const deriveStages = (auditData = []) => {
  const signals = new Set(auditData.map((r) => r.signal));
  const has = (...args) => args.some((s) => signals.has(s));
  const latestFrom = (rows, ...args) => {
    const matches = rows
      .filter((r) => args.includes(r.signal))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return matches.length > 0 ? matches[0].signal : null;
  };
  const latest = (...args) => latestFrom(auditData, ...args);
  const allFilesDownloadedEvents = auditData.filter(
    (r) => r.signal === "All files downloaded"
  );
  const allFilesDownloadedAt = allFilesDownloadedEvents.length
    ? new Date(
      Math.min(...allFilesDownloadedEvents.map((r) => new Date(r.createdAt).getTime()))
    )
    : null;
  const isPreDownloadPause = (r) =>
    r.signal === "Ota campaign paused" &&
    (!allFilesDownloadedAt || new Date(r.createdAt) <= allFilesDownloadedAt);
  const isPostDownloadPause = (r) =>
    r.signal === "Ota campaign paused" &&
    allFilesDownloadedAt &&
    new Date(r.createdAt) > allFilesDownloadedAt;

  const matchedFor = (key) =>
    (auditData || [])
      .filter((r) => STAGE_SIGNAL_MAP[key]?.includes(r.signal))
      .filter((r) => {
        if (r.signal !== "Ota campaign paused") return true;
        if (key === "download") return isPreDownloadPause(r);
        if (key === "installation") return isPostDownloadPause(r);
        return true;
      })
      .reduce((acc, row) => {
        if (!acc.find((x) => x.signal === row.signal)) acc.push(row);
        return acc;
      }, [])
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const versionMatched = has("Version matched");
  const downloadOccurred = has("All files downloaded");
  const verificationOccurred = has("Verification success");
  const consentOccurred = has("User accepts install");
  const skipDownload = versionMatched && !downloadOccurred;
  const skipVerification = versionMatched && !verificationOccurred;
  const skipConsent = versionMatched && !consentOccurred;

  return [
    {
      key: "version",
      label: "Version check",
      badge: has(
        "Version matched",
        "Version check disabled",
        "Version compatible",
        "Version upgradable",
      )
        ? "Passed"
        : has(
          "Version not compatible",
          "Version not upgradable",
          "Device version is higher than campaign version",
        )
          ? "Failed"
          : "Pending",
      status: has(
        "Version matched",
        "Version check disabled",
        "Version compatible",
        "Version upgradable",
      )
        ? "done"
        : has(
          "Version not compatible",
          "Version not upgradable",
          "Device version is higher than campaign version",
        )
          ? "failed"
          : "pending",
      signals: matchedFor("version"),
    },
    {
      key: "download",
      label: "Download",
      badge: (() => {
        if (skipDownload) return "Skipped";
        if (has("All files downloaded")) return "Complete";
        if (
          has(
            "Download failed",
            "Download authentication failed",
            "Storage space insufficient",
          )
        )
          return "Failed";

        const lastUserAction = latest(
          "Download consent popup shown",
          "User accepts download",
          "User rejects download",
          "User deferred download",
          "Cancel downloading",
          "Resume downloading",
          "Data setting enabled on Wi-Fi",
          "Data setting disabled on Wi-Fi",
          "Ota campaign paused",
        );
        if (lastUserAction === "Download consent popup shown")
          return "Awaiting";
        if (
          lastUserAction === "User rejects download" ||
          lastUserAction === "Cancel downloading"
        )
          return "Rejected";
        if (lastUserAction === "User deferred download") return "Deferred";
        if (
          lastUserAction === "Data setting disabled on Wi-Fi" ||
          lastUserAction === "Ota campaign paused"
        )
          return "Paused";
        if (
          lastUserAction === "Resume downloading" ||
          lastUserAction === "User accepts download" ||
          lastUserAction === "Data setting enabled on Wi-Fi"
        )
          return "In progress";

        return "Pending";
      })(),
      status: (() => {
        if (skipDownload) return "done";
        if (has("All files downloaded")) return "done";
        if (
          has(
            "Download failed",
            "Download authentication failed",
            "Storage space insufficient",
          )
        )
          return "failed";

        const lastUserAction = latest(
          "Download consent popup shown",
          "User accepts download",
          "User rejects download",
          "User deferred download",
          "Cancel downloading",
          "Resume downloading",
          "Data setting enabled on Wi-Fi",
          "Data setting disabled on Wi-Fi",
          "Ota campaign paused",
        );
        if (lastUserAction === "Download consent popup shown") return "paused";
        if (
          lastUserAction === "User rejects download" ||
          lastUserAction === "Cancel downloading"
        )
          return "failed";
        if (lastUserAction === "User deferred download") return "deferred";
        if (
          lastUserAction === "Data setting disabled on Wi-Fi" ||
          lastUserAction === "Ota campaign paused"
        )
          return "paused";
        if (
          lastUserAction === "Resume downloading" ||
          lastUserAction === "User accepts download" ||
          lastUserAction === "Data setting enabled on Wi-Fi"
        )
          return "active";

        return "pending";
      })(),
      note: skipDownload
        ? "Skipped — device already on target version, no download needed"
        : undefined,
      signals: matchedFor("download"),
    },
    {
      key: "verification",
      label: "Verification",
      badge: (() => {
        if (skipVerification) return "Skipped";
        const lastAction = latest(
          "Verification success",
          "Verification failed",
          "User Accepts Verification",
          "User Rejects Verification",
          "Verification started",
        );

        if (
          lastAction === "Verification success" ||
          lastAction === "User Accepts Verification"
        )
          return "Success";
        if (
          lastAction === "Verification failed" ||
          lastAction === "User Rejects Verification"
        )
          return "Rejected";
        if (lastAction === "Verification started") return "In progress";
        return "Pending";
      })(),
      status: (() => {
        if (skipVerification) return "done";
        const lastAction = latest(
          "Verification success",
          "Verification failed",
          "User Accepts Verification",
          "User Rejects Verification",
          "Verification started",
        );

        if (
          lastAction === "Verification success" ||
          lastAction === "User Accepts Verification"
        )
          return "done";
        if (
          lastAction === "Verification failed" ||
          lastAction === "User Rejects Verification"
        )
          return "failed";
        if (lastAction === "Verification started") return "active";
        return "pending";
      })(),
      note: skipVerification
        ? "Skipped — device already on target version, no verification needed"
        : undefined,
      signals: matchedFor("verification"),
    },
    {
      key: "consent",
      label: "Install consent",
      badge: (() => {
        if (skipConsent) return "Skipped";
        const lastConsentSignal = latest(
          "User accepts install",
          "User rejects install",
          "User deferred install",
          "Installation precondition not met",
          "Installation precondition met",
          "Install consent popup shown",
        );
        if (lastConsentSignal === "User accepts install") return "Accepted";
        if (lastConsentSignal === "User rejects install") return "Rejected";
        if (lastConsentSignal === "User deferred install") return "Deferred";
        if (lastConsentSignal === "Installation precondition not met")
          return "Blocked";
        if (
          lastConsentSignal === "Installation precondition met" ||
          lastConsentSignal === "Install consent popup shown"
        )
          return "Awaiting";
        return "Pending";
      })(),
      status: (() => {
        if (skipConsent) return "done";
        const lastConsentSignal = latest(
          "User accepts install",
          "User rejects install",
          "User deferred install",
          "Installation precondition not met",
          "Installation precondition met",
          "Install consent popup shown",
        );
        if (lastConsentSignal === "User accepts install") return "done";
        if (lastConsentSignal === "User rejects install") return "failed";
        if (lastConsentSignal === "User deferred install") return "deferred";
        if (lastConsentSignal === "Installation precondition not met")
          return "paused";
        if (
          lastConsentSignal === "Installation precondition met" ||
          lastConsentSignal === "Install consent popup shown"
        )
          return "paused";
        return "pending";
      })(),
      note: skipConsent
        ? "Skipped — device already on target version, no install consent needed"
        : undefined,
      signals: matchedFor("consent"),
    },
    {
      key: "installation",
      label: "Installation",
      badge: (() => {
        const installationRows = auditData.filter((r) => !isPreDownloadPause(r));
        const lastAction = latestFrom(
          installationRows,
          "Installation success",
          "Complete",
          "Installation failed",
          "Ota campaign paused",
          "In progress",
          "Version matched"
        );

        if (lastAction === "Installation success" || lastAction === "Complete" || lastAction === "Version matched")
          return "Success";
        if (lastAction === "Installation failed") return "Failed";
        const allDownloadedTime = auditData
          .filter((r) => r.signal === "All files downloaded")
          .map((r) => new Date(r.eventTime))
          .sort((a, b) => b - a)[0];

        const pausedAfterDownload =
          auditData.filter(
            (r) =>
              r.signal === "Ota campaign paused" &&
              allDownloadedTime &&
              new Date(r.eventTime) > allDownloadedTime,
          ).length > 0;
        if (pausedAfterDownload) return "paused";
        if (lastAction === "In progress") return "In progress";
        if (has("User accepts install")) return "In progress";
        return "Pending";
      })(),
      status: (() => {
        const installationRows = auditData.filter((r) => !isPreDownloadPause(r));
        const lastAction = latestFrom(
          installationRows,
          "Installation success",
          "Complete",
          "Installation failed",
          "Ota campaign paused",
          "In progress",
          "Version matched"
        );

        if (lastAction === "Installation success" || lastAction === "Complete" || lastAction === "Version matched")
          return "done";
        if (lastAction === "Installation failed") return "failed";
        const allDownloadedTime = auditData
          .filter((r) => r.signal === "All files downloaded")
          .map((r) => new Date(r.eventTime))
          .sort((a, b) => b - a)[0];

        const pausedAfterDownload =
          auditData.filter(
            (r) =>
              r.signal === "Ota campaign paused" &&
              allDownloadedTime &&
              new Date(r.eventTime) > allDownloadedTime,
          ).length > 0;

        if (pausedAfterDownload) return "paused";
        if (lastAction === "In progress") return "active";

        if (has("User accepts install")) return "active";

        return "pending";
      })(),
      note: versionMatched
        ? "Device already running the target version — no install needed"
        : undefined,
      signals: matchedFor("installation"),
    },
  ];
};

const BADGE_SX = {
  done: { bgcolor: "#e6f4ea", color: "#1e7e34" },
  active: { bgcolor: "#e3f2fd", color: "#1565c0" },
  failed: { bgcolor: "#fdecea", color: "#c62828" },
  pending: { bgcolor: "#f5f5f5", color: "#9e9e9e" },
  deferred: { bgcolor: "#fff8e1", color: "#f57f17" },
  paused: { bgcolor: "#f3e5f5", color: "#6a1b9a" },
};

const StatusDot = ({ status }) => {
  if (status === "done")
    return <CheckCircleIcon sx={{ color: "#1D9E75", fontSize: 24 }} />;
  if (status === "failed")
    return <ErrorIcon sx={{ color: "#E24B4A", fontSize: 24 }} />;
  if (status === "active")
    return (
      <CheckCircleIcon
        sx={{
          color: "#378ADD",
          fontSize: 24,
          "@keyframes pulse": {
            "0%,100%": { opacity: 1 },
            "50%": { opacity: 0.5 },
          },
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />
    );
  if (status === "deferred")
    return <PauseCircleOutlineIcon sx={{ color: "#f57f17", fontSize: 24 }} />;
  if (status === "paused")
    return <PauseCircleOutlineIcon sx={{ color: "#9c27b0", fontSize: 24 }} />;
  return <RadioButtonUncheckedIcon sx={{ color: "#bdbdbd", fontSize: 24 }} />;
};

const StageTooltipContent = ({ stage }) => {
  if (!stage.signals || stage.signals.length === 0) {
    return (
      <Box sx={{ p: 0.5, maxWidth: 240 }}>
        <Typography sx={{ fontSize: 12, color: "#aaa" }}>
          {stage.note || "No events yet"}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 0.5, minWidth: 200, maxWidth: 280 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#fff",
          mb: 0.75,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          opacity: 0.7,
        }}
      >
        {stage.label}
      </Typography>
      {stage.signals.map((s, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            mb: 0.5,
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 13, color: "#1D9E75", mt: "2px", flexShrink: 0 }}
          />
          <Box>
            <Typography sx={{ fontSize: 12, color: "#fff", lineHeight: 1.4 }}>
              {s.signal}
            </Typography>
            {s.createdAt && (
              <Typography sx={{ fontSize: 10, color: "#aaa", lineHeight: 1.3 }}>
                {formatDate_12h(s.createdAt)}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DeploymentProgressTracker = ({ auditData = [] }) => {
  const [auditOpen, setAuditOpen] = useState(false);
  const stages = deriveStages(auditData);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
            Deployment progress
          </Typography>
          {auditData.length > 0 && (
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
            >
              {auditData.length} audit events recorded
            </Typography>
          )}
        </Box>

        {auditData.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            endIcon={
              auditOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
            }
            onClick={() => setAuditOpen((v) => !v)}
            sx={{
              textTransform: "none",
              fontSize: 12,
              borderColor: "#e0e0e0",
              color: "text.secondary",
              "&:hover": { borderColor: "#bdbdbd" },
            }}
          >
            Job audit
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {stages.map((stage, idx) => (
          <Box
            key={stage.key}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor:
                    idx === 0
                      ? "transparent"
                      : stages[idx - 1].status === "done"
                        ? "#1D9E75"
                        : "#e0e0e0",
                }}
              />
              <Tooltip
                title={<StageTooltipContent stage={stage} />}
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#1e1e2e",
                      "& .MuiTooltip-arrow": { color: "#1e1e2e" },
                      borderRadius: "8px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                      p: 1,
                    },
                  },
                }}
              >
                <Box sx={{ cursor: "pointer", lineHeight: 0 }}>
                  <StatusDot status={stage.status} />
                </Box>
              </Tooltip>
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor:
                    idx === stages.length - 1
                      ? "transparent"
                      : stage.status === "done"
                        ? "#1D9E75"
                        : "#e0e0e0",
                }}
              />
            </Box>

            <Box sx={{ mt: 1, textAlign: "center", px: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color:
                    stage.status === "pending"
                      ? "text.disabled"
                      : "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {stage.label}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  mt: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  fontSize: 10,
                  fontWeight: 500,
                  ...BADGE_SX[stage.status],
                }}
              >
                {stage.badge}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Collapse in={auditOpen}>
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: "text.disabled",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Job audit log
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 260, overflow: "auto", borderRadius: 1 }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Event Time</TableCell>
                  <TableCell>Android Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Package Type</TableCell>
                  <TableCell>Status Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditData.map((row, i) => (
                  <TableRow
                    key={row.vehicleDeploymentAuditId ?? i}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell>{formatDate_12h(row?.createdAt)}</TableCell>
                    <TableCell>{formatDate_12h(row?.eventTime)}</TableCell>
                    <TableCell>{row.signal}</TableCell>
                    <TableCell>{row.packageType}</TableCell>
                    <TableCell>{row.jobStatusMessage}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DeploymentProgressTracker;
